/**
 * Policy templates — extensible governance presets (Part 5).
 *
 * A template is a coherent, named header patch spanning every scalar policy
 * domain, plus operator-facing metadata: where it sits on the autonomy ↔
 * friction trade-off, and which compliance frameworks it's shaped for.
 *
 * Relationship to POSTURES (posture.ts): postures are the three "quick dials"
 * surfaced inline in the console shell (conservative / balanced / aggressive).
 * Templates are the richer gallery — they SUPERSET postures (Balanced here ==
 * the balanced posture) and add compliance-oriented presets (SOC 2, ISO 27001,
 * Healthcare, Financial, High-security) that no single dial captures.
 *
 * EXTENSIBILITY: adding a framework is ONE registry entry. Structure + levels +
 * compliance tags live here (client-safe, no i18n); display copy (label /
 * description / trade-offs) lives in the `settingsGovernance.templates.<k>`
 * i18n namespace, resolved by the model — exactly the model.ts pattern. The
 * header patch is the only behavioural surface, so applying a template is a
 * single header save that recompiles the runtime knobs (compile.ts) — no new
 * code path, fully reversible by applying another template.
 */
import type { GovernanceHeader } from "./types";

export type TemplateCategory = "general" | "compliance";

export type GovernanceTemplate = {
  id: string;
  category: TemplateCategory;
  /** i18n stem under settingsGovernance.templates.<k> for label/desc/tradeoffs. */
  k: string;
  /** lucide icon name (resolved in the UI, like the domain icons). */
  icon: string;
  /** The coherent header patch this template applies. */
  header: Partial<GovernanceHeader>;
  /** 0 (fully gated) … 100 (fully autonomous) — the resulting AI autonomy. */
  autonomyLevel: number;
  /** 0 (frictionless) … 100 (heavy) — the resulting operational friction. */
  frictionLevel: number;
  /** Compliance frameworks this template is shaped for (proper nouns, not localized). */
  compliance: string[];
};

const SHORT_RETENTION = { retentionTraceDays: 30, retentionApprovalDays: 90, retentionAuditDays: 90, retentionRuntimeLogDays: 14, retentionCommsDays: 90 };
const STD_RETENTION = { retentionTraceDays: 90, retentionApprovalDays: 180, retentionAuditDays: 365, retentionRuntimeLogDays: 30, retentionCommsDays: 180 };
const LONG_RETENTION = { retentionTraceDays: 180, retentionApprovalDays: 365, retentionAuditDays: 730, retentionRuntimeLogDays: 90, retentionCommsDays: 365 };
const REG_1Y = { retentionTraceDays: 365, retentionApprovalDays: 365, retentionAuditDays: 365, retentionRuntimeLogDays: 180, retentionCommsDays: 365 };
const HIPAA_6Y = { retentionTraceDays: 2190, retentionApprovalDays: 2190, retentionAuditDays: 2190, retentionRuntimeLogDays: 730, retentionCommsDays: 2190 };
const SOX_7Y = { retentionTraceDays: 2555, retentionApprovalDays: 2555, retentionAuditDays: 2555, retentionRuntimeLogDays: 365, retentionCommsDays: 2555 };

export const GOVERNANCE_TEMPLATES: GovernanceTemplate[] = [
  {
    id: "startup", category: "general", k: "startup", icon: "Rocket",
    autonomyLevel: 85, frictionLevel: 18, compliance: [],
    header: {
      autonomyMode: "aggressive", approvalRequirement: "risky_only",
      approvalTimeoutMinutes: 15, approvalTimeoutAction: "notify_fallback",
      verificationStrictness: "standard", requireSandbox: true, requireTests: false,
      requireQaSignoff: false, blockProdWithoutQa: false,
      commsMode: "draft_only", commsEnforced: false,
      ...SHORT_RETENTION,
    },
  },
  {
    id: "balanced", category: "general", k: "balanced", icon: "Scale",
    autonomyLevel: 60, frictionLevel: 42, compliance: [],
    header: {
      autonomyMode: "balanced", approvalRequirement: "risky_only",
      approvalTimeoutMinutes: 30, approvalTimeoutAction: "escalate",
      verificationStrictness: "standard", requireSandbox: true, requireTests: true,
      requireQaSignoff: false, blockProdWithoutQa: true,
      commsMode: "draft_only", commsEnforced: false,
      ...STD_RETENTION,
    },
  },
  {
    id: "enterprise", category: "general", k: "enterprise", icon: "Building2",
    autonomyLevel: 38, frictionLevel: 62, compliance: [],
    header: {
      autonomyMode: "conservative", approvalRequirement: "production_impact",
      approvalTimeoutMinutes: 60, approvalTimeoutAction: "escalate",
      verificationStrictness: "strict", requireSandbox: true, requireTests: true,
      requireQaSignoff: true, blockProdWithoutQa: true,
      commsMode: "draft_only", commsEnforced: true,
      ...LONG_RETENTION,
    },
  },
  {
    id: "soc2", category: "compliance", k: "soc2", icon: "ShieldCheck",
    autonomyLevel: 32, frictionLevel: 70, compliance: ["SOC 2"],
    header: {
      autonomyMode: "conservative", approvalRequirement: "production_impact",
      approvalTimeoutMinutes: 120, approvalTimeoutAction: "escalate",
      verificationStrictness: "strict", requireSandbox: true, requireTests: true,
      requireQaSignoff: true, blockProdWithoutQa: true,
      commsMode: "never_autosend", commsEnforced: true,
      ...REG_1Y,
    },
  },
  {
    id: "iso27001", category: "compliance", k: "iso27001", icon: "Lock",
    autonomyLevel: 30, frictionLevel: 72, compliance: ["ISO 27001"],
    header: {
      autonomyMode: "conservative", approvalRequirement: "security_sensitive",
      approvalTimeoutMinutes: 120, approvalTimeoutAction: "escalate",
      verificationStrictness: "strict", requireSandbox: true, requireTests: true,
      requireQaSignoff: true, blockProdWithoutQa: true,
      commsMode: "never_autosend", commsEnforced: true,
      ...REG_1Y,
    },
  },
  {
    id: "healthcare", category: "compliance", k: "healthcare", icon: "HeartPulse",
    autonomyLevel: 16, frictionLevel: 88, compliance: ["HIPAA"],
    header: {
      autonomyMode: "conservative", approvalRequirement: "always",
      approvalTimeoutMinutes: 240, approvalTimeoutAction: "escalate",
      verificationStrictness: "strict", requireSandbox: true, requireTests: true,
      requireQaSignoff: true, blockProdWithoutQa: true,
      commsMode: "never_autosend", commsEnforced: true,
      ...HIPAA_6Y,
    },
  },
  {
    id: "financial", category: "compliance", k: "financial", icon: "Landmark",
    autonomyLevel: 22, frictionLevel: 82, compliance: ["SOX", "PCI-DSS"],
    header: {
      autonomyMode: "conservative", approvalRequirement: "production_impact",
      approvalTimeoutMinutes: 240, approvalTimeoutAction: "escalate",
      verificationStrictness: "strict", requireSandbox: true, requireTests: true,
      requireQaSignoff: true, blockProdWithoutQa: true,
      commsMode: "never_autosend", commsEnforced: true,
      ...SOX_7Y,
    },
  },
  {
    id: "high_security", category: "compliance", k: "highSecurity", icon: "ShieldAlert",
    autonomyLevel: 10, frictionLevel: 92, compliance: [],
    header: {
      autonomyMode: "conservative", approvalRequirement: "always",
      approvalTimeoutMinutes: 0, approvalTimeoutAction: "escalate",
      verificationStrictness: "strict", requireSandbox: true, requireTests: true,
      requireQaSignoff: true, blockProdWithoutQa: true,
      commsMode: "never_autosend", commsEnforced: true,
      ...REG_1Y,
    },
  },
];

export const TEMPLATES_BY_ID: Record<string, GovernanceTemplate> =
  Object.fromEntries(GOVERNANCE_TEMPLATES.map((tpl) => [tpl.id, tpl]));

/** The fields that define a template's identity (categorical, not numeric). */
const TEMPLATE_SIGNATURE = [
  "autonomyMode", "approvalRequirement", "verificationStrictness",
  "requireQaSignoff", "blockProdWithoutQa", "commsMode", "commsEnforced",
] as const satisfies ReadonlyArray<keyof GovernanceHeader>;

/**
 * Which template (if any) the current header matches. Compliance templates are
 * checked before general ones so the most specific (stricter) preset wins when
 * signatures overlap. Returns the template id or null (= custom).
 */
export function matchTemplate(header: GovernanceHeader): string | null {
  const ordered = [...GOVERNANCE_TEMPLATES].sort(
    (a, b) => (a.category === "compliance" ? -1 : 1) - (b.category === "compliance" ? -1 : 1),
  );
  for (const tpl of ordered) {
    if (TEMPLATE_SIGNATURE.every((f) => tpl.header[f] === undefined || tpl.header[f] === header[f])) {
      return tpl.id;
    }
  }
  return null;
}
